import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users, Target, Briefcase, Heart, TrendingUp, Search,
  ChevronRight, GraduationCap, BarChart3, Bell, LogOut,
  Compass, Eye, Award, Zap, Star, ArrowLeft, PenTool,
  MessageCircle, Send, Loader2, FileText, Brain, X,
  CheckCircle, Calendar, ChevronDown, Plus
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@/contexts/ThemeContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const hdr = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' });

const GPSCounselorDashboard = ({ user, setUser }) => {
  const navigate = useNavigate();
  const { colors } = useTheme();
  const [dashData, setDashData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [snapshotLoading, setSnapshotLoading] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [noteForm, setNoteForm] = useState({ note: '', category: 'general' });
  const [aiInsights, setAiInsights] = useState(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchDashboard(); fetchAIInsights(); }, []);

  const fetchAIInsights = async () => {
    try {
      const res = await fetch(`${API_URL}/api/gps/counselor/ai-insights`, { headers: hdr() });
      if (res.ok) setAiInsights(await res.json());
    } catch (e) { console.error(e); }
  };

  const fetchDashboard = async () => {
    try {
      const res = await fetch(`${API_URL}/api/gps/counselor/dashboard`, { headers: hdr() });
      if (res.ok) setDashData(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const openStudentSnapshot = async (studentId) => {
    setSelectedStudent(studentId);
    setSnapshotLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/gps/counselor/student/${studentId}`, { headers: hdr() });
      if (res.ok) setSnapshot(await res.json());
    } catch (e) { console.error(e); }
    setSnapshotLoading(false);
  };

  const submitNote = async () => {
    if (!noteForm.note.trim()) { toast.error('Write a note first'); return; }
    try {
      const res = await fetch(`${API_URL}/api/gps/counselor/notes`, {
        method: 'POST', headers: hdr(),
        body: JSON.stringify({ student_id: selectedStudent, ...noteForm })
      });
      if (res.ok) {
        toast.success('Note saved');
        setShowNoteModal(false);
        setNoteForm({ note: '', category: 'general' });
        openStudentSnapshot(selectedStudent);
      }
    } catch (e) { toast.error('Failed to save note'); }
  };

  const handleLogout = () => { localStorage.removeItem('token'); setUser(null); };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F5F3EF' }}>
      <GraduationCap className="w-12 h-12 animate-pulse" style={{ color: colors.primary }} />
    </div>
  );

  const stats = dashData?.stats || {};
  const students = dashData?.students || [];
  const filteredStudents = students.filter(s =>
    s.name.toLowerCase().includes(searchTerm.toLowerCase()) || s.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getEngagement = (s) => {
    const score = (s.interests_count > 0 ? 1 : 0) + (s.skills_count > 0 ? 1 : 0) + (s.active_goals > 0 ? 1 : 0) + (s.career_explorations > 0 ? 1 : 0) + (s.activities_count > 0 ? 1 : 0);
    if (score >= 4) return { label: 'Thriving', color: '#10B981' };
    if (score >= 2) return { label: 'Engaged', color: '#3B82F6' };
    if (score >= 1) return { label: 'Starting', color: '#F59E0B' };
    return { label: 'Needs Outreach', color: '#EF4444' };
  };

  // Student Snapshot View
  if (selectedStudent && snapshot) {
    const s = snapshot.student || {};
    const p = snapshot.profile || {};
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#F5F3EF' }}>
        <div style={{ background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primary_dark || colors.primary} 100%)` }}>
          <div className="max-w-4xl mx-auto p-4">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => { setSelectedStudent(null); setSnapshot(null); }} className="text-gray-900 hover:bg-gray-100 rounded-full">
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-3 flex-1">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center text-xl bg-white/15">{s.avatar_emoji || '🎓'}</div>
                <div className="text-white">
                  <h1 className="text-xl font-bold">{s.name}</h1>
                  <p className="text-gray-700 text-sm">{s.email} {s.grade ? `| Grade ${s.grade}` : ''}</p>
                </div>
              </div>
              <Button size="sm" onClick={() => setShowNoteModal(true)} className="bg-white/20 text-gray-900 hover:bg-white/30 rounded-lg" data-testid="add-note-btn">
                <PenTool className="w-4 h-4 mr-1" /> Add Note
              </Button>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 py-5 space-y-4">
          {/* Quick Stats */}
          <div className="grid grid-cols-5 gap-2">
            {[
              { label: 'Points', val: s.total_points || 0, icon: Star, color: '#F59E0B' },
              { label: 'Interests', val: (p.interests || []).length, icon: Heart, color: '#EC4899' },
              { label: 'Skills', val: (p.skills || []).length, icon: Zap, color: '#F59E0B' },
              { label: 'Goals', val: (snapshot.goals || []).length, icon: Target, color: '#10B981' },
              { label: 'Careers', val: (snapshot.career_explorations || []).length, icon: Briefcase, color: '#8B5CF6' },
            ].map(st => (
              <Card key={st.label} className="p-3 rounded-xl text-center" style={{ backgroundColor: colors.card, borderColor: colors.border }}>
                <st.icon className="w-4 h-4 mx-auto mb-1" style={{ color: st.color }} />
                <p className="text-lg font-bold" style={{ color: colors.text }}>{st.val}</p>
                <p className="text-[10px]" style={{ color: colors.text_muted }}>{st.label}</p>
              </Card>
            ))}
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {/* Interests */}
            <Card className="p-4 rounded-2xl" style={{ backgroundColor: colors.card, borderColor: colors.border }}>
              <h3 className="font-bold text-sm mb-3 flex items-center gap-2" style={{ color: colors.text }}><Heart className="w-4 h-4" style={{ color: '#EC4899' }} /> Interests</h3>
              {(p.interests || []).length === 0 ? <p className="text-sm" style={{ color: colors.text_muted }}>None yet</p> : (
                <div className="flex flex-wrap gap-1">{(p.interests || []).map((int, i) => <Badge key={i} className="text-xs" style={{ backgroundColor: '#EC489910', color: '#EC4899' }}>{int}</Badge>)}</div>
              )}
            </Card>

            {/* Goals */}
            <Card className="p-4 rounded-2xl" style={{ backgroundColor: colors.card, borderColor: colors.border }}>
              <h3 className="font-bold text-sm mb-3 flex items-center gap-2" style={{ color: colors.text }}><Target className="w-4 h-4" style={{ color: '#10B981' }} /> Goals ({(snapshot.goals || []).length})</h3>
              {(snapshot.goals || []).length === 0 ? <p className="text-sm" style={{ color: colors.text_muted }}>No goals set</p> : (
                <div className="space-y-2">{(snapshot.goals || []).slice(0, 5).map(g => (
                  <div key={g.id} className="flex items-center gap-2 p-2 rounded-lg" style={{ backgroundColor: colors.surface }}>
                    {g.status === 'completed' ? <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" /> : <div className="w-4 h-4 rounded-full border-2 flex-shrink-0" style={{ borderColor: '#10B981' }} />}
                    <div className="flex-1 min-w-0"><p className="text-xs font-semibold truncate" style={{ color: colors.text }}>{g.title}</p></div>
                  </div>
                ))}</div>
              )}
            </Card>

            {/* Career Explorations */}
            <Card className="p-4 rounded-2xl" style={{ backgroundColor: colors.card, borderColor: colors.border }}>
              <h3 className="font-bold text-sm mb-3 flex items-center gap-2" style={{ color: colors.text }}><Briefcase className="w-4 h-4" style={{ color: '#8B5CF6' }} /> Careers Explored</h3>
              {(snapshot.career_explorations || []).length === 0 ? <p className="text-sm" style={{ color: colors.text_muted }}>None yet</p> : (
                <div className="space-y-2">{(snapshot.career_explorations || []).map(c => (
                  <div key={c.id} className="p-2 rounded-lg" style={{ backgroundColor: colors.surface }}>
                    <p className="text-xs font-semibold" style={{ color: colors.text }}>{c.career_title}</p>
                    <p className="text-[10px]" style={{ color: colors.text_muted }}>{c.salary_range} | {c.education_path}</p>
                  </div>
                ))}</div>
              )}
            </Card>

            {/* Activities */}
            <Card className="p-4 rounded-2xl" style={{ backgroundColor: colors.card, borderColor: colors.border }}>
              <h3 className="font-bold text-sm mb-3 flex items-center gap-2" style={{ color: colors.text }}><Users className="w-4 h-4" style={{ color: colors.primary }} /> Activities ({(snapshot.activities || []).length})</h3>
              {(snapshot.activities || []).length === 0 ? <p className="text-sm" style={{ color: colors.text_muted }}>No activities</p> : (
                <div className="space-y-2">{(snapshot.activities || []).map(a => (
                  <div key={a.id} className="flex items-center gap-2 p-2 rounded-lg" style={{ backgroundColor: colors.surface }}>
                    <span className="text-sm">{a.category === 'sport' ? '⚽' : a.category === 'club' ? '🎭' : '📌'}</span>
                    <div><p className="text-xs font-semibold" style={{ color: colors.text }}>{a.title}</p><p className="text-[10px]" style={{ color: colors.text_muted }}>{a.role || a.category}</p></div>
                  </div>
                ))}</div>
              )}
            </Card>
          </div>

          {/* Counselor Notes */}
          <Card className="p-4 rounded-2xl" style={{ backgroundColor: colors.card, borderColor: colors.border }}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-sm flex items-center gap-2" style={{ color: colors.text }}><PenTool className="w-4 h-4" style={{ color: '#6366F1' }} /> My Notes</h3>
              <Button size="sm" variant="outline" onClick={() => setShowNoteModal(true)} className="text-xs h-7 rounded-lg"><Plus className="w-3 h-3 mr-1" /> Note</Button>
            </div>
            {(snapshot.counselor_notes || []).length === 0 ? (
              <p className="text-sm text-center py-3" style={{ color: colors.text_muted }}>No notes yet. Add your first note about this student.</p>
            ) : (
              <div className="space-y-2">{(snapshot.counselor_notes || []).map(n => (
                <div key={n.id} className="p-3 rounded-xl" style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
                  <div className="flex items-center gap-2 mb-1">
                    <Badge className="text-[10px]" style={{ backgroundColor: `${colors.primary}10`, color: colors.primary }}>{n.category}</Badge>
                    <span className="text-[10px]" style={{ color: colors.text_muted }}>{new Date(n.created_at).toLocaleDateString()}</span>
                  </div>
                  <p className="text-sm" style={{ color: colors.text }}>{n.note}</p>
                </div>
              ))}</div>
            )}
          </Card>

          {/* Post-Secondary Plans */}
          {(snapshot.post_secondary || []).length > 0 && (
            <Card className="p-4 rounded-2xl" style={{ backgroundColor: colors.card, borderColor: colors.border }}>
              <h3 className="font-bold text-sm flex items-center gap-2 mb-3" style={{ color: colors.text }}><GraduationCap className="w-4 h-4" style={{ color: '#6366F1' }} /> Post-Secondary Plans</h3>
              {(snapshot.post_secondary || []).map(ps => (
                <div key={ps.id} className="p-2 rounded-lg mb-1" style={{ backgroundColor: colors.surface }}>
                  <p className="text-xs font-semibold" style={{ color: colors.text }}>{ps.school_name}</p>
                  <p className="text-[10px]" style={{ color: colors.text_muted }}>{ps.school_type?.replace('_', ' ')} | {ps.application_status?.replace('_', ' ')}</p>
                </div>
              ))}
            </Card>
          )}
        </div>

        {/* Note Modal */}
        <Dialog open={showNoteModal} onOpenChange={setShowNoteModal}>
          <DialogContent className="rounded-2xl">
            <DialogHeader><DialogTitle>Add Note for {snapshot?.student?.name}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <Select value={noteForm.category} onValueChange={v => setNoteForm({...noteForm, category: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['general', 'academic', 'career', 'social-emotional', 'family', 'follow-up'].map(c => <SelectItem key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1).replace('-', ' ')}</SelectItem>)}
                </SelectContent>
              </Select>
              <Textarea value={noteForm.note} onChange={e => setNoteForm({...noteForm, note: e.target.value})} placeholder="Write your note..." rows={4} />
              <Button onClick={submitNote} className="w-full" style={{ backgroundColor: colors.primary }}>Save Note</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Main Dashboard View
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F5F3EF' }}>
      <div style={{ background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primary_dark || colors.primary} 100%)` }}>
        <div className="max-w-5xl mx-auto p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center bg-white/15"><GraduationCap className="w-6 h-6 text-white" /></div>
              <div>
                <h1 className="text-xl font-bold text-white" data-testid="counselor-title">{user?.program_name || 'Student Pathways GPS'}</h1>
                <p className="text-gray-700 text-sm">School Counselor — {user?.name}</p>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-gray-700 hover:text-gray-900 hover:bg-gray-100"><LogOut className="w-5 h-5" /></Button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-5">
            {[
              { label: 'Students', val: dashData?.total_students || 0 },
              { label: 'With Goals', val: stats.students_with_goals || 0 },
              { label: 'Careers Explored', val: stats.total_career_explorations || 0 },
              { label: 'Interests Mapped', val: stats.students_with_interests || 0 },
              { label: 'Journal Entries', val: stats.total_journal_entries || 0 },
            ].map(s => (
              <div key={s.label} className="p-3 rounded-xl text-center" style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}>
                <p className="text-2xl font-bold text-white">{s.val}</p>
                <p className="text-gray-600 text-xs">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-5">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="w-full rounded-xl p-1 mb-5" style={{ backgroundColor: colors.card }}>
            <TabsTrigger value="overview" className="flex-1 rounded-lg data-[state=active]:bg-blue-600 data-[state=active]:text-white"><BarChart3 className="w-4 h-4 mr-2" /> Overview</TabsTrigger>
            <TabsTrigger value="students" className="flex-1 rounded-lg data-[state=active]:bg-blue-600 data-[state=active]:text-white"><Users className="w-4 h-4 mr-2" /> Students</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-4">
            <div className="grid md:grid-cols-3 gap-4">
              {[
                { title: 'Interests Discovery', icon: Heart, color: '#EC4899', val: stats.avg_interests || 0, label: 'Avg per student', pct: (stats.students_with_interests || 0) / Math.max(dashData?.total_students, 1) * 100, sub: `${stats.students_with_interests || 0}/${dashData?.total_students || 0} engaged` },
                { title: 'Goal Setting', icon: Target, color: '#10B981', val: stats.avg_goals || 0, label: 'Avg per student', pct: (stats.students_with_goals || 0) / Math.max(dashData?.total_students, 1) * 100, sub: `${stats.students_with_goals || 0}/${dashData?.total_students || 0} with goals` },
                { title: 'Career Exploration', icon: Briefcase, color: '#8B5CF6', val: stats.total_career_explorations || 0, label: 'Total explored', pct: 100, sub: 'AI-Powered Guidance' },
              ].map(card => (
                <Card key={card.title} className="p-4 rounded-2xl" style={{ backgroundColor: colors.card, borderColor: colors.border }}>
                  <div className="flex items-center gap-2 mb-3"><card.icon className="w-4 h-4" style={{ color: card.color }} /><h3 className="font-bold text-sm" style={{ color: colors.text }}>{card.title}</h3></div>
                  <p className="text-2xl font-bold" style={{ color: colors.text }}>{card.val}</p>
                  <p className="text-xs" style={{ color: colors.text_muted }}>{card.label}</p>
                  <Progress value={card.pct} className="mt-2 h-1.5" />
                  <p className="text-[10px] mt-1" style={{ color: colors.text_muted }}>{card.sub}</p>
                </Card>
              ))}
            </div>

            {/* AI Insights Panel */}
            {aiInsights && (aiInsights.alerts?.length > 0 || aiInsights.celebrations?.length > 0 || aiInsights.recommendations?.length > 0) && (
              <Card className="p-5 rounded-2xl" style={{ background: 'linear-gradient(135deg, #EDE9FE 0%, #FEF3C7 100%)', border: '1px solid #DDD6FE' }}>
                <div className="flex items-center gap-2 mb-4">
                  <Brain className="w-5 h-5 text-orange-600" />
                  <h3 className="font-bold text-sm text-orange-900">AI Insights for You</h3>
                </div>
                {aiInsights.alerts?.map((a, i) => (
                  <div key={`a${i}`} onClick={() => openStudentSnapshot(a.student_id)} className="flex items-start gap-3 p-3 rounded-xl bg-red-50 border border-red-100 mb-2 cursor-pointer hover:shadow-sm transition-all">
                    <span className="text-lg flex-shrink-0">{a.avatar || '🎓'}</span>
                    <div className="flex-1"><p className="text-xs font-semibold text-red-800">{a.student_name}</p><p className="text-xs text-red-700">{a.message}</p></div>
                    <Badge className="text-[9px] bg-red-100 text-red-700 flex-shrink-0">{a.priority}</Badge>
                  </div>
                ))}
                {aiInsights.celebrations?.map((c, i) => (
                  <div key={`c${i}`} onClick={() => openStudentSnapshot(c.student_id)} className="flex items-start gap-3 p-3 rounded-xl bg-green-50 border border-green-100 mb-2 cursor-pointer hover:shadow-sm transition-all">
                    <span className="text-lg flex-shrink-0">{c.avatar || '🎓'}</span>
                    <div><p className="text-xs font-semibold text-green-800">{c.student_name}</p><p className="text-xs text-green-700">{c.message}</p></div>
                  </div>
                ))}
                {aiInsights.recommendations?.map((r, i) => (
                  <div key={`r${i}`} onClick={() => openStudentSnapshot(r.student_id)} className="flex items-start gap-3 p-3 rounded-xl bg-blue-50 border border-blue-100 mb-2 cursor-pointer hover:shadow-sm transition-all">
                    <span className="text-lg flex-shrink-0">{r.avatar || '🎓'}</span>
                    <div><p className="text-xs font-semibold text-blue-800">{r.student_name}</p><p className="text-xs text-blue-700">{r.message}</p></div>
                  </div>
                ))}
              </Card>
            )}

            {/* Needs Outreach */}
            <Card className="p-4 rounded-2xl" style={{ backgroundColor: colors.card, borderColor: colors.border }}>
              <h3 className="font-bold text-sm flex items-center gap-2 mb-3" style={{ color: colors.text }}><Eye className="w-4 h-4" style={{ color: '#EF4444' }} /> Students Needing Outreach</h3>
              <div className="space-y-2">
                {students.filter(s => getEngagement(s).label === 'Needs Outreach').map(s => (
                  <div key={s.id} className="flex items-center justify-between p-3 rounded-xl cursor-pointer hover:shadow-sm" style={{ backgroundColor: colors.surface }} onClick={() => openStudentSnapshot(s.id)} data-testid={`outreach-${s.id}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center text-lg" style={{ backgroundColor: '#FEE2E2' }}>{s.avatar_emoji || '🎓'}</div>
                      <div><p className="font-semibold text-sm" style={{ color: colors.text }}>{s.name}</p><p className="text-xs" style={{ color: colors.text_muted }}>No activity yet</p></div>
                    </div>
                    <Button size="sm" variant="outline" className="text-xs">View Profile</Button>
                  </div>
                ))}
                {students.filter(s => getEngagement(s).label === 'Needs Outreach').length === 0 && (
                  <p className="text-center py-3 text-sm" style={{ color: colors.text_muted }}>All students are engaged!</p>
                )}
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="students" className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: colors.text_muted }} />
              <Input placeholder="Search students..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                className="pl-10 rounded-xl" style={{ backgroundColor: colors.card, borderColor: colors.border, color: colors.text }} data-testid="student-search" />
            </div>
            <div className="space-y-2">
              {filteredStudents.map((s, idx) => {
                const eng = getEngagement(s);
                return (
                  <motion.div key={s.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.04 }}>
                    <Card className="p-4 rounded-2xl cursor-pointer hover:shadow-md transition-shadow" style={{ backgroundColor: colors.card, borderColor: colors.border }}
                      onClick={() => openStudentSnapshot(s.id)} data-testid={`student-card-${s.id}`}>
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl flex items-center justify-center text-xl" style={{ backgroundColor: colors.surface }}>{s.avatar_emoji || '🎓'}</div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2"><p className="font-bold" style={{ color: colors.text }}>{s.name}</p><Badge className="text-[10px]" style={{ backgroundColor: `${eng.color}15`, color: eng.color }}>{eng.label}</Badge></div>
                          <p className="text-xs truncate" style={{ color: colors.text_muted }}>{s.email}</p>
                          <div className="flex gap-3 mt-1.5 flex-wrap">
                            {[
                              { icon: Heart, val: s.interests_count, label: 'interests' },
                              { icon: Target, val: s.active_goals, label: 'goals' },
                              { icon: Briefcase, val: s.career_explorations, label: 'careers' },
                              { icon: Users, val: s.activities_count, label: 'activities' },
                              { icon: Star, val: s.total_points, label: 'pts' },
                            ].map((m, i) => (
                              <span key={i} className="text-[10px] flex items-center gap-0.5" style={{ color: colors.text_muted }}>
                                <m.icon className="w-3 h-3" /> {m.val} {m.label}
                              </span>
                            ))}
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5 flex-shrink-0" style={{ color: colors.text_muted }} />
                      </div>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default GPSCounselorDashboard;
